import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { Material, PortalClass } from '../../types/portal';
import { Plus, CreditCard as Edit2, Trash2, X, Check, Eye, EyeOff, Upload, FileText, Loader2, Search } from 'lucide-react';

const cardClass =
  'bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.05)]';
const inputClass =
  'w-full px-4 py-3 border border-[#ddd6cf] rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none transition-colors duration-200 bg-white';
const primaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-[#970000] text-white hover:bg-[#7a0000] transition-colors duration-200 font-medium text-sm';
const secondaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full border border-[#d9c9bd] text-[#5f5148] hover:bg-[#f8f3ef] transition-colors duration-200 font-medium text-sm';
const dangerBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 font-medium text-sm';

const emptyForm = () => ({
  class_ids: [] as string[],
  title: '',
  description: '',
  material_type: 'link' as 'pdf' | 'link',
  url: '',
  is_visible: true,
});

const MaterialsManager: React.FC = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [opError, setOpError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    const [{ data: mData }, { data: cData }, { data: mcData }] = await Promise.all([
      supabase.from('materials').select('*').order('created_at', { ascending: false }),
      supabase.from('portal_classes').select('*').eq('is_active', true).order('title'),
      supabase.from('material_classes').select('material_id, class_id'),
    ]);

    const classIdsByMaterial: Record<string, string[]> = {};
    for (const row of (mcData ?? []) as Array<{ material_id: string; class_id: string }>) {
      (classIdsByMaterial[row.material_id] ??= []).push(row.class_id);
    }

    setMaterials(
      ((mData ?? []) as Material[]).map(m => ({
        ...m,
        classIds: classIdsByMaterial[m.id] ?? (m.class_id ? [m.class_id] : []),
      }))
    );
    setClasses((cData ?? []) as PortalClass[]);
    setLoading(false);
  };

  const classMap = Object.fromEntries(classes.map(c => [c.id, c.title]));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return materials;
    return materials.filter(m => {
      const clsNames = (m.classIds ?? [])
        .map(id => (classMap[id] ?? '').toLowerCase())
        .join(' ');
      return (
        m.title.toLowerCase().includes(q) ||
        (m.description ?? '').toLowerCase().includes(q) ||
        clsNames.includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, search, classes]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setPdfFile(null);
    setShowForm(true);
  };
  const openEdit = (m: Material) => {
    setEditId(m.id);
    setForm({
      class_ids: m.classIds ?? (m.class_id ? [m.class_id] : []),
      title: m.title,
      description: m.description ?? '',
      material_type: m.material_type,
      url: m.url,
      is_visible: m.is_visible,
    });
    setPdfFile(null);
    setShowForm(true);
  };

  const toggleFormClass = (classId: string) => {
    setForm(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId],
    }));
  };

  const syncMaterialClasses = async (materialId: string, classIds: string[]) => {
    await supabase.from('material_classes').delete().eq('material_id', materialId);
    if (classIds.length > 0) {
      const { error } = await supabase
        .from('material_classes')
        .insert(classIds.map(class_id => ({ material_id: materialId, class_id })));
      if (error) throw error;
    }
  };

  const MAX_PDF_BYTES = 25 * 1024 * 1024;

  const uploadPdf = async (file: File): Promise<{ path: string; url: string }> => {
    if (file.size > MAX_PDF_BYTES) {
      throw new Error(`Datei zu groß (max. ${MAX_PDF_BYTES / (1024 * 1024)} MB).`);
    }
    if (file.type !== 'application/pdf' || !file.name.toLowerCase().endsWith('.pdf')) {
      throw new Error('Nur PDF-Dateien sind erlaubt.');
    }
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.pdf`;
    const { error } = await supabase.storage.from('material-files').upload(path, file, {
      contentType: 'application/pdf',
      upsert: false,
    });
    if (error) throw error;
    // Bucket is private; url is kept for legacy display but real downloads
    // happen via short-lived signed URLs derived from storage_path.
    const { data } = supabase.storage.from('material-files').getPublicUrl(path);
    return { path, url: data.publicUrl };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.class_ids.length === 0 || !form.title.trim()) return;
    if (form.material_type === 'link' && !form.url.trim()) return;
    if (form.material_type === 'pdf' && !pdfFile && !form.url) return;

    setSaving(true);
    try {
      let url = form.url;
      let storagePath: string | null = null;

      if (form.material_type === 'pdf' && pdfFile) {
        setUploading(true);
        const uploaded = await uploadPdf(pdfFile);
        url = uploaded.url;
        storagePath = uploaded.path;
        setUploading(false);
      }

      const payload: Record<string, unknown> = {
        class_id: form.class_ids[0],
        title: form.title.trim(),
        description: form.description.trim() || null,
        material_type: form.material_type,
        url,
        is_visible: form.is_visible,
        updated_at: new Date().toISOString(),
      };
      if (form.material_type === 'link') {
        payload.storage_path = null;
      } else if (storagePath) {
        payload.storage_path = storagePath;
      }
      if (editId) {
        const { error } = await supabase.from('materials').update(payload).eq('id', editId);
        if (error) throw error;
        await syncMaterialClasses(editId, form.class_ids);
        setMaterials(prev =>
          prev.map(m => (m.id === editId ? { ...m, ...payload, classIds: form.class_ids } : m))
        );
      } else {
        const { data, error } = await supabase.from('materials').insert(payload).select().single();
        if (error) throw error;
        const created = data as Material;
        await syncMaterialClasses(created.id, form.class_ids);
        setMaterials(prev => [{ ...created, classIds: form.class_ids }, ...prev]);
      }
      setShowForm(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      const hint = /bucket|not found|404/i.test(msg)
        ? ' Hinweis: Der Storage-Bucket "material-files" fehlt vermutlich. Bitte die Migration 20260519000005 in Supabase ausführen.'
        : /policy|permission|row-level|unauthorized|403/i.test(msg)
          ? ' Hinweis: Keine Berechtigung zum Hochladen. Prüfe, ob dein Konto Portal-Admin ist.'
          : '';
      setOpError(`Fehler beim Speichern: ${msg}${hint}`);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const mat = materials.find(m => m.id === id);
    if (mat?.storage_path) {
      await supabase.storage.from('material-files').remove([mat.storage_path]);
    }
    const { error } = await supabase.from('materials').delete().eq('id', id);
    if (!error) setMaterials(prev => prev.filter(m => m.id !== id));
    setConfirmDelete(null);
  };

  const toggleVisible = async (m: Material) => {
    const { error } = await supabase
      .from('materials')
      .update({ is_visible: !m.is_visible, updated_at: new Date().toISOString() })
      .eq('id', m.id);
    if (!error)
      setMaterials(prev =>
        prev.map(mat => (mat.id === m.id ? { ...mat, is_visible: !m.is_visible } : mat))
      );
  };

  const f =
    (field: keyof ReturnType<typeof emptyForm>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-1">
            Portal
          </p>
          <h2 className="text-3xl font-bold text-[#1f1f1f]">Materialien</h2>
        </div>
        <button onClick={openCreate} className={primaryBtn}>
          <Plus className="h-4 w-4 mr-1" />
          Hinzufügen
        </button>
      </div>

      {opError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{opError}</span>
          <button onClick={() => setOpError('')} className="ml-4 text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#eadfd6]">
              <h3 className="text-lg font-bold text-[#1f1f1f]">
                {editId ? 'Material bearbeiten' : 'Neues Material'}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-xl hover:bg-[#f8f3ef]">
                <X className="h-5 w-5 text-[#6b6b6b]" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                  Klassen * <span className="font-normal text-[#9b9490]">(Mehrfachauswahl möglich)</span>
                </label>

                {/* Ausgewählte Klassen als entfernbare Chips */}
                {form.class_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.class_ids.map(id => {
                      const cls = classes.find(c => c.id === id);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-[#970000]/10 text-[#970000] border border-[#970000]/20"
                        >
                          {cls?.title ?? 'Unbekannte Klasse'}
                          <button
                            type="button"
                            onClick={() => toggleFormClass(id)}
                            className="hover:bg-red-100 rounded-full p-0.5 -mr-1"
                            title="Entfernen"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                <select
                  value=""
                  onChange={e => { if (e.target.value) toggleFormClass(e.target.value); }}
                  className={inputClass}
                  disabled={classes.length === 0}
                >
                  <option value="">
                    {classes.length === 0 ? 'Keine aktiven Klassen' : 'Klasse hinzufügen…'}
                  </option>
                  {classes
                    .filter(c => !form.class_ids.includes(c.id))
                    .map(c => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                </select>
                {form.class_ids.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Bitte mindestens eine Klasse wählen.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Titel *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={f('title')}
                  className={inputClass}
                  required
                  placeholder="Titel des Materials"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Typ *</label>
                <select
                  value={form.material_type}
                  onChange={e => {
                    const val = e.target.value as 'pdf' | 'link';
                    setForm(p => ({ ...p, material_type: val, url: '' }));
                    setPdfFile(null);
                  }}
                  className={inputClass}
                >
                  <option value="link">Link (URL)</option>
                  <option value="pdf">PDF-Datei hochladen</option>
                </select>
              </div>

              {form.material_type === 'pdf' ? (
                <div>
                  <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                    PDF-Datei *
                  </label>
                  {form.url && !pdfFile && (
                    <div className="flex items-center gap-2 mb-2 p-3 bg-[#faf7f5] rounded-xl border border-[#eadfd6]">
                      <FileText className="h-4 w-4 text-[#970000] shrink-0" />
                      <span className="text-xs text-[#5f5148] truncate flex-1">
                        Aktuell: {form.url.split('/').pop()}
                      </span>
                      <span className="text-xs text-[#9b9490]">(neue Datei wählen zum Ersetzen)</span>
                    </div>
                  )}
                  {pdfFile ? (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                      <FileText className="h-4 w-4 text-emerald-700 shrink-0" />
                      <span className="text-sm font-medium text-emerald-700 flex-1 truncate">
                        {pdfFile.name}
                      </span>
                      <span className="text-xs text-emerald-600">
                        {(pdfFile.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                      <button
                        type="button"
                        onClick={() => setPdfFile(null)}
                        className="text-emerald-700 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex flex-col items-center gap-2 p-6 border-2 border-dashed border-[#ddd6cf] rounded-2xl hover:border-[#970000] hover:bg-[#faf7f5] transition-colors"
                    >
                      <Upload className="h-6 w-6 text-[#9b9490]" />
                      <span className="text-sm font-medium text-[#5f5148]">
                        PDF auswählen
                      </span>
                      <span className="text-xs text-[#9b9490]">Maximal 50 MB</span>
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) setPdfFile(file);
                      e.target.value = '';
                    }}
                  />
                  {!form.url && !pdfFile && (
                    <p className="text-xs text-red-500 mt-1">Bitte eine PDF-Datei auswählen.</p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">URL *</label>
                  <input
                    type="url"
                    value={form.url}
                    onChange={f('url')}
                    className={inputClass}
                    required
                    placeholder="https://…"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                  Beschreibung
                </label>
                <textarea
                  value={form.description}
                  onChange={f('description')}
                  className={inputClass + ' resize-none'}
                  rows={2}
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_visible}
                  onChange={e => setForm(p => ({ ...p, is_visible: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm font-medium">Sichtbar für Schüler</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className={primaryBtn}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      {uploading ? 'Wird hochgeladen…' : 'Speichert…'}
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Speichern
                    </>
                  )}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className={secondaryBtn}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] p-6 w-full max-w-sm">
            <p className="font-semibold text-[#1f1f1f] mb-4">Material wirklich löschen?</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(confirmDelete)} className={dangerBtn}>
                Löschen
              </button>
              <button onClick={() => setConfirmDelete(null)} className={secondaryBtn}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={cardClass + ' p-4'}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9b9490]" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Nach Titel oder Kurs suchen…"
            className={inputClass + ' pl-10'}
          />
        </div>
      </div>

      <div className={cardClass + ' overflow-hidden'}>
        {loading ? (
          <div className="p-10 text-center text-[#6b6b6b]">Wird geladen…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#6b6b6b]">
            {materials.length === 0 ? 'Keine Materialien vorhanden.' : 'Keine Materialien gefunden.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eadfd6]">
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Titel</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Typ</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Klassen</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Sichtbar</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id} className="border-b border-[#eadfd6] hover:bg-[#faf8f6]">
                    <td className="p-4">
                      <p className="font-medium text-[#1f1f1f]">{m.title}</p>
                      {m.description && (
                        <p className="text-xs text-[#6b6b6b] mt-0.5 line-clamp-1">
                          {m.description}
                        </p>
                      )}
                    </td>
                    <td className="p-4">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          m.material_type === 'pdf'
                            ? 'bg-red-50 text-red-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}
                      >
                        {m.material_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 text-[#6b6b6b]">
                      {(m.classIds ?? []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(m.classIds ?? []).map(cid => (
                            <span
                              key={cid}
                              className="text-xs px-2 py-0.5 rounded-lg bg-[#970000]/10 text-[#970000] border border-[#970000]/20"
                            >
                              {classMap[cid] ?? '–'}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td className="p-4">
                      <button onClick={() => toggleVisible(m)}>
                        {m.is_visible ? (
                          <Eye className="h-4 w-4 text-green-600" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-[#9b9490]" />
                        )}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(m)} className={secondaryBtn}>
                          <Edit2 className="h-3.5 w-3.5 mr-1" />
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => setConfirmDelete(m.id)}
                          className="p-2 rounded-xl text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialsManager;
